/**
 * 顶栏。在线数来自设备注册表,告警数来自 hub 的告警判定 —— 两个都是真的。
 *
 * 原型这里的红黄数字是 mock 常量,顶在每页最显眼处;删掉之后一度是空的,
 * 现在 hub 有了告警表(fleetstate),它回来了,而且点得动:跳到总览的告警区。
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTerminals, useAlerts } from '@/hooks/useHubData';
import { useAuth } from '@/hooks/useAuth';

export default function TopBar({ onMenu }: { onMenu: () => void }) {
  const { terminals, error } = useTerminals();
  const { counts, unavailable: alertsOff } = useAlerts();
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const [now, setNow] = useState(() => new Date());
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  const online = terminals.filter((t) => t.status === 'online').length;

  return (
    <header className="fixed left-0 lg:left-56 right-0 top-0 h-14 bg-background-50/95 backdrop-blur-sm border-b border-background-200/70 flex items-center justify-between px-3 sm:px-6 z-30">
      <div className="flex items-center gap-2 min-w-0">
        <button onClick={onMenu} className="w-10 h-10 flex items-center justify-center rounded-lg hover:bg-background-100 cursor-pointer lg:hidden">
          <i className="ri-menu-line text-foreground-600 text-lg" />
        </button>
        <h2 className="text-sm font-semibold text-foreground-800 font-heading truncate">舰队管理控制台</h2>
      </div>

      <div className="flex items-center gap-3 sm:gap-5">
        {/* 读不到就说读不到,不显示 0/0 —— 那看起来像"一台设备都没有" */}
        <div className="flex items-center gap-1.5 text-xs">
          {error ? (
            <span className="text-red-600" title={error}><i className="ri-error-warning-line" /> 设备列表读取失败</span>
          ) : (
            <>
              <span className="w-2 h-2 rounded-full bg-accent-500"></span>
              <span className="font-mono font-medium text-foreground-600">{online}</span>
              <span className="text-foreground-400">/</span>
              <span className="font-mono text-foreground-600">{terminals.length}</span>
              <span className="text-foreground-400 hidden sm:inline">在线</span>
            </>
          )}
        </div>

        {/* 告警数:未配置告警功能时**不显示 0** —— 那会读成"一切正常" */}
        {!alertsOff && ((counts.critical ?? 0) > 0 || (counts.warning ?? 0) > 0) && (
          <button onClick={() => navigate('/console')} className="flex items-center gap-2 text-xs cursor-pointer">
            {(counts.critical ?? 0) > 0 && (
              <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-red-500" />
                <span className="font-mono font-medium text-red-600">{counts.critical}</span>
                <span className="text-foreground-500 hidden sm:inline">严重</span></span>
            )}
            {(counts.warning ?? 0) > 0 && (
              <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-amber-500" />
                <span className="font-mono font-medium text-amber-600">{counts.warning}</span>
                <span className="text-foreground-500 hidden sm:inline">警告</span></span>
            )}
          </button>
        )}

        <div className="w-px h-5 bg-background-200/70 hidden sm:block"></div>
        <div className="text-xs font-mono text-foreground-500 hidden sm:block">
          {now.toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
        </div>

        <div className="relative">
          <button onClick={() => setMenuOpen(!menuOpen)} className="flex items-center gap-2 cursor-pointer py-1">
            <span className="w-8 h-8 rounded-full bg-secondary-200 flex items-center justify-center">
              <i className="ri-user-line text-sm text-foreground-600"></i>
            </span>
            <span className="text-xs text-foreground-600 font-medium hidden sm:inline">{user?.kind === 'admin' ? '座舱' : user?.id ?? '未登录'}</span>
            <i className="ri-arrow-down-s-line text-xs text-foreground-400"></i>
          </button>
          {menuOpen && (
            <>
              <div className="fixed inset-0 z-30" onClick={() => setMenuOpen(false)} />
              <div className="absolute right-0 top-11 z-40 w-48 bg-background-50 border border-background-200/70 rounded-lg shadow-lg py-1">
                <div className="px-3 py-2 border-b border-background-200/70">
                  <p className="text-[11px] text-foreground-400">当前身份</p>
                  <p className="text-xs font-mono text-foreground-700 break-all">{user ? `${user.kind} · ${user.id}` : '未登录'}</p>
                </div>
                <button onClick={async () => { await signOut(); navigate('/login', { replace: true }); }}
                  className="w-full text-left px-3 py-2.5 text-xs text-foreground-600 hover:bg-background-100 cursor-pointer">
                  退出登录(清除本机令牌)
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </header>
  );
}
